/**
 * Registro de módulos del dashboard. **Fuente única de la navegación.**
 *
 * Los nueve módulos son los de `.claude/skills/xyvona-frontend/SKILL.md`.
 * Cada uno declara el issue que lo construye, para que la barra lateral diga
 * la verdad: lo que todavía no existe aparece deshabilitado y marcado, no
 * como un enlace que lleva a una pantalla vacía.
 *
 * Cómo activar un módulo cuando lo terminás
 * -----------------------------------------
 * Una sola línea acá: `status: 'pending'` → `'ready'` en TU entrada. Las
 * rutas no se tocan: se descubren solas desde `routes/<modulo>/routes.tsx`
 * (ver `routes/module.ts`).
 *
 * Este archivo NO deriva el estado del registro de rutas, aunque se podría:
 * los componentes de las páginas importan `nav.ts` para `HOME_PATH`, así que
 * un glob eager acá cerraría el ciclo nav → rutas → página → nav. Una línea
 * declarada es más barata que un ciclo de imports.
 *
 * `minRole` filtra: un `viewer` no ve "Administración" en el menú. Es
 * ergonomía —la autorización la hace la API—, pero también evita el ticket
 * de "me aparece una sección que no puedo abrir".
 */
import type { Role } from '@/api/types';
import type { MessageKey } from '@/i18n/messages';

export type ModuleStatus = 'ready' | 'pending';

/**
 * Las secciones del menú, en el orden en que se muestran.
 *
 * Agrupar no es cosmética: con dieciséis módulos en una lista plana, encontrar
 * "Activación" cuesta leer los dieciséis. Los grupos son los del trabajo real
 * —qué hay en el catálogo, qué pasa en planta, qué pasó, qué hay que
 * investigar, cómo está configurado— y no los del organigrama.
 *
 * `hq` va última y separada: es el panel de XYVONA, no del cliente. Para una
 * marca esa sección no existe y `visibleModules()` no la devuelve.
 */
export const NAV_SECTIONS = [
  'main',
  'catalog',
  'plant',
  'activity',
  'protection',
  'config',
  'hq',
] as const;

export type NavSection = (typeof NAV_SECTIONS)[number];

/** Encabezado de cada sección. `main` no lleva: es el punto de entrada. */
export const SECTION_LABEL_KEYS: Record<NavSection, MessageKey | null> = {
  main: null,
  catalog: 'nav.section.catalog',
  plant: 'nav.section.plant',
  activity: 'nav.section.activity',
  protection: 'nav.section.protection',
  config: 'nav.section.config',
  hq: 'nav.section.hq',
};

export interface NavModule {
  id: string;
  /** Ruta absoluta. En inglés, como los recursos de la API. */
  path: string;
  labelKey: MessageKey;
  descriptionKey: MessageKey;
  /** Rol mínimo para verlo en el menú. */
  minRole: Role;
  status: ModuleStatus;
  /** En qué grupo del menú aparece. */
  section: NavSection;
  /** Issue de GitHub que lo construye. `null` si todavía no tiene uno. */
  issue: number | null;
  /**
   * Sólo para el tenant de back-office (`xyvona`). Es el panel con el que
   * XYVONA administra a sus clientes: para una marca no existe, y la API le
   * responde 404 —no 403— a quien lo pida sin serlo.
   *
   * Es ergonomía, como `minRole`: quien manipule el bundle va a ver los
   * nombres de las rutas igual. Lo que protege es `backoffice_guard()` del
   * lado de Postgres.
   */
  backoffice?: boolean;
}

const DECLARED_MODULES: readonly NavModule[] = [
  {
    id: 'overview',
    path: '/overview',
    labelKey: 'module.overview',
    descriptionKey: 'module.overview.description',
    minRole: 'viewer',
    section: 'main',
    status: 'ready',
    issue: null,
  },
  {
    id: 'map',
    path: '/map',
    labelKey: 'module.map',
    descriptionKey: 'module.map.description',
    minRole: 'viewer',
    section: 'activity',
    status: 'ready',
    issue: 43,
  },
  {
    id: 'products',
    path: '/products',
    labelKey: 'module.products',
    descriptionKey: 'module.products.description',
    minRole: 'viewer',
    section: 'catalog',
    status: 'ready',
    issue: 38,
  },
  {
    id: 'batches',
    path: '/batches',
    labelKey: 'module.batches',
    descriptionKey: 'module.batches.description',
    minRole: 'viewer',
    section: 'catalog',
    status: 'ready',
    issue: 39,
  },
  {
    // No está en la lista de nueve módulos de la skill: es la consola de la
    // línea de empaque (#41), y es la que explica por qué una etiqueta robada
    // no autentica. Metida dentro de "Etiquetas" quedaba escondida detrás de
    // órdenes y rollos, que es otro trabajo y otra persona.
    id: 'activation',
    path: '/activation',
    labelKey: 'module.activation',
    descriptionKey: 'module.activation.description',
    // Activar es escribir: `POST /activations/bulk` exige `analyst`.
    minRole: 'analyst',
    section: 'plant',
    status: 'ready',
    issue: 41,
  },
  {
    id: 'alerts',
    path: '/alerts',
    labelKey: 'module.alerts',
    descriptionKey: 'module.alerts.description',
    // Un `viewer` puede leer la bandeja; operarla (asignar, cerrar) es de
    // `analyst` para arriba, y eso se controla dentro del módulo, no acá.
    minRole: 'viewer',
    section: 'protection',
    status: 'ready',
    issue: 42,
  },
  {
    id: 'labels',
    path: '/labels',
    labelKey: 'module.labels',
    descriptionKey: 'module.labels.description',
    minRole: 'analyst',
    section: 'catalog',
    status: 'ready',
    issue: 40,
  },
  {
    id: 'analytics',
    path: '/analytics',
    labelKey: 'module.analytics',
    descriptionKey: 'module.analytics.description',
    minRole: 'viewer',
    section: 'activity',
    status: 'ready',
    issue: 44,
  },
  {
    id: 'reports',
    path: '/reports',
    labelKey: 'module.reports',
    descriptionKey: 'module.reports.description',
    // Un reporte formal lleva la firma de quien lo generó y pide
    // exportaciones (`POST /api/v1/exports` exige `analyst`): un `viewer` no
    // podría completar la acción principal de la vista.
    minRole: 'analyst',
    section: 'activity',
    status: 'ready',
    issue: 47,
  },
  {
    // Los mensajes que ve el consumidor (#90). No está en la lista de nueve
    // módulos de la skill porque no es un módulo de datos: es la única
    // pantalla del dashboard que edita lo que se publica en la página
    // pública. Va después de "Etiquetas" y antes de "Analítica" porque se
    // configura una vez, al dar de alta la marca, y casi no se vuelve.
    id: 'verification',
    path: '/verification',
    labelKey: 'module.verification',
    descriptionKey: 'module.verification.description',
    // Leer la copia publicada es trabajo de calidad; escribirla exige `admin`
    // y lo aplica la API, no el menú.
    minRole: 'viewer',
    section: 'config',
    status: 'ready',
    issue: 90,
  },
  {
    id: 'integrations',
    path: '/integrations',
    labelKey: 'module.integrations',
    descriptionKey: 'module.integrations.description',
    // API keys y webhooks son credenciales de integración: sólo admin.
    minRole: 'admin',
    section: 'config',
    status: 'pending',
    issue: 28,
  },
  {
    id: 'admin',
    path: '/admin',
    labelKey: 'module.admin',
    descriptionKey: 'module.admin.description',
    minRole: 'admin',
    // Parcial: `/admin/users` ya funciona contra `GET /api/v1/auth/users` y
    // es la vista de referencia del andamio. Roles, SSO y auditoría son #45.
    section: 'config',
    status: 'ready',
    issue: 45,
  },
  // ───────────────────────────────────── back-office (#109), sólo XYVONA
  {
    id: 'clients',
    path: '/hq/clients',
    labelKey: 'module.clients',
    descriptionKey: 'module.clients.description',
    minRole: 'admin',
    section: 'hq',
    status: 'ready',
    issue: 109,
    backoffice: true,
  },
  {
    id: 'requests',
    path: '/hq/requests',
    labelKey: 'module.requests',
    descriptionKey: 'module.requests.description',
    minRole: 'admin',
    section: 'hq',
    status: 'ready',
    issue: 109,
    backoffice: true,
  },
  // El stock de etiquetas (#109) dejó de ser un módulo del menú: es la pestaña
  // «Stock» de Catálogo › Etiquetas (#170). `/hq/stock` redirige.
  {
    id: 'tooling',
    path: '/hq/tooling',
    labelKey: 'module.tooling',
    descriptionKey: 'module.tooling.description',
    minRole: 'admin',
    section: 'hq',
    status: 'ready',
    issue: 147,
    backoffice: true,
  },
];

export const NAV_MODULES: readonly NavModule[] = DECLARED_MODULES;

/** El slug del tenant de back-office. El mismo de `routers/backoffice.py`. */
export const BACKOFFICE_SLUG = 'xyvona';

/** Ruta a la que se entra después del login. */
export const HOME_PATH = '/overview';

/** Una sección con los módulos que le quedaron después de filtrar. */
export interface NavGroup {
  section: NavSection;
  labelKey: MessageKey | null;
  modules: NavModule[];
}

/**
 * Los módulos visibles, agrupados y en el orden de `NAV_SECTIONS`.
 *
 * Una sección que queda vacía NO se devuelve: un encabezado sin nada debajo es
 * peor que no tener encabezado, porque parece que algo no cargó. Le pasa a
 * `hq` en cuanto entra una marca, y a `plant` con un `viewer`.
 */
export function groupedModules(
  role: Role | null | undefined,
  rank: (role: Role | null | undefined, minimum: Role) => boolean,
  tenant?: string | null,
): NavGroup[] {
  const visibles = visibleModules(role, rank, tenant);
  return NAV_SECTIONS.map((section) => ({
    section,
    labelKey: SECTION_LABEL_KEYS[section],
    modules: visibles.filter((module) => module.section === section),
  })).filter((group) => group.modules.length > 0);
}

/** Los módulos que este rol —y este tenant— pueden ver en el menú. */
export function visibleModules(
  role: Role | null | undefined,
  rank: (role: Role | null | undefined, minimum: Role) => boolean,
  tenant?: string | null,
): NavModule[] {
  return NAV_MODULES.filter(
    (module) =>
      rank(role, module.minRole) &&
      (!module.backoffice || tenant === BACKOFFICE_SLUG),
  );
}
